import React from 'react';
import { 
  Home, Truck, Package, ShoppingBag, Wrench, Shirt,
  MapPin, Clock, Weight, FileText, Camera
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';

// ===== CLEANING SERVICE FORM =====
export const CleaningServiceForm = ({ data, onChange }) => {
  const propertyTypes = ['apartment', 'house', 'condo', 'townhouse', 'office', 'commercial'];
  const cleaningAreas = ['Kitchen', 'Bathrooms', 'Bedrooms', 'Living Room', 'Dining Room', 'Office', 'Garage', 'Laundry Room', 'Outdoor'];
  
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const toggleArea = (area) => {
    const current = data.areas_to_clean || [];
    const updated = current.includes(area) 
      ? current.filter(a => a !== area)
      : [...current, area];
    handleChange('areas_to_clean', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
        <Home className="w-5 h-5" />
        Property Details
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Property Type *</Label>
          <Select value={data.property_type || ''} onValueChange={(v) => handleChange('property_type', v)}>
            <SelectTrigger className="input-styled mt-1">
              <SelectValue placeholder="Select property type" />
            </SelectTrigger>
            <SelectContent>
              {propertyTypes.map(type => (
                <SelectItem key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Approximate Square Footage</Label>
          <Input
            type="number"
            value={data.square_footage || ''}
            onChange={(e) => handleChange('square_footage', e.target.value)}
            placeholder="e.g., 1500"
            className="input-styled mt-1"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label>Bedrooms</Label>
          <Input
            type="number"
            min="0"
            value={data.bedrooms || ''}
            onChange={(e) => handleChange('bedrooms', parseInt(e.target.value) || 0)}
            className="input-styled mt-1"
          />
        </div>
        <div>
          <Label>Bathrooms</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={data.bathrooms || ''}
            onChange={(e) => handleChange('bathrooms', parseFloat(e.target.value) || 0)}
            className="input-styled mt-1"
          />
        </div>
        <div>
          <Label>Floors/Levels</Label>
          <Input
            type="number"
            min="1"
            value={data.floors || 1}
            onChange={(e) => handleChange('floors', parseInt(e.target.value) || 1)}
            className="input-styled mt-1"
          />
        </div>
      </div>

      <div>
        <Label className="mb-3 block">Areas to Clean *</Label>
        <div className="flex flex-wrap gap-2">
          {cleaningAreas.map(area => (
            <button
              key={area}
              type="button"
              onClick={() => toggleArea(area.toLowerCase())}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                (data.areas_to_clean || []).includes(area.toLowerCase())
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              {area}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="flex items-center gap-3 p-3 bg-[var(--accent)] rounded-lg cursor-pointer">
          <Checkbox
            checked={data.pets || false}
            onCheckedChange={(checked) => handleChange('pets', checked)}
          />
          <span>I have pets in the home</span>
        </label>
        <label className="flex items-center gap-3 p-3 bg-[var(--accent)] rounded-lg cursor-pointer">
          <Checkbox
            checked={data.supplies_provided || false}
            onCheckedChange={(checked) => handleChange('supplies_provided', checked)}
          />
          <span>I will provide cleaning supplies</span>
        </label>
      </div>

      {data.pets && (
        <div>
          <Label>Pet Details</Label>
          <Input
            value={data.pet_details || ''}
            onChange={(e) => handleChange('pet_details', e.target.value)}
            placeholder="Type of pets, any concerns..."
            className="input-styled mt-1"
          />
        </div>
      )}

      <div>
        <Label>Special Instructions</Label>
        <Textarea
          value={data.special_instructions || ''}
          onChange={(e) => handleChange('special_instructions', e.target.value)}
          placeholder="Any specific areas of concern, access instructions, or special requests..."
          className="input-styled mt-1"
          rows={3}
        />
      </div>
    </div>
  );
};

// ===== LAUNDRY SERVICE FORM =====
export const LaundryServiceForm = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const laundryTypes = ['Regular Clothes', 'Delicates', 'Bedding/Linens', 'Towels', 'Work Uniforms', 'Formal Wear', 'Sports Gear'];

  const toggleLaundryType = (type) => {
    const current = data.laundry_types || [];
    const updated = current.includes(type) 
      ? current.filter(t => t !== type)
      : [...current, type];
    handleChange('laundry_types', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
        <Shirt className="w-5 h-5" />
        Laundry Details
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Estimated Number of Loads *</Label>
          <Select value={data.estimated_loads?.toString() || ''} onValueChange={(v) => handleChange('estimated_loads', parseInt(v))}>
            <SelectTrigger className="input-styled mt-1">
              <SelectValue placeholder="Select load count" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <SelectItem key={num} value={num.toString()}>{num} load{num > 1 ? 's' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Service Type *</Label>
          <Select value={data.service_level || ''} onValueChange={(v) => handleChange('service_level', v)}>
            <SelectTrigger className="input-styled mt-1">
              <SelectValue placeholder="Select service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="wash_fold">Wash & Fold</SelectItem>
              <SelectItem value="wash_iron">Wash & Iron</SelectItem>
              <SelectItem value="dry_clean">Dry Cleaning</SelectItem>
              <SelectItem value="full_service">Full Service (Wash, Dry, Fold, Iron)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="mb-3 block">What types of laundry? *</Label>
        <div className="flex flex-wrap gap-2">
          {laundryTypes.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => toggleLaundryType(type.toLowerCase().replace(/ /g, '_'))}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                (data.laundry_types || []).includes(type.toLowerCase().replace(/ /g, '_'))
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="flex items-center gap-3 p-3 bg-[var(--accent)] rounded-lg cursor-pointer">
          <Checkbox
            checked={data.pickup_required || false}
            onCheckedChange={(checked) => handleChange('pickup_required', checked)}
          />
          <span>Pickup from my location</span>
        </label>
        <label className="flex items-center gap-3 p-3 bg-[var(--accent)] rounded-lg cursor-pointer">
          <Checkbox
            checked={data.delivery_required || false}
            onCheckedChange={(checked) => handleChange('delivery_required', checked)}
          />
          <span>Deliver back to me</span>
        </label>
      </div>

      <div>
        <Label>Detergent Preference</Label>
        <Select value={data.detergent_preference || ''} onValueChange={(v) => handleChange('detergent_preference', v)}>
          <SelectTrigger className="input-styled mt-1">
            <SelectValue placeholder="Select preference" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="standard">Standard (We Provide)</SelectItem>
            <SelectItem value="hypoallergenic">Hypoallergenic</SelectItem>
            <SelectItem value="fragrance_free">Fragrance-Free</SelectItem>
            <SelectItem value="customer_provided">I Will Provide My Own</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Special Instructions</Label>
        <Textarea
          value={data.special_instructions || ''}
          onChange={(e) => handleChange('special_instructions', e.target.value)}
          placeholder="Any stain concerns, color separation preferences, or special care items..."
          className="input-styled mt-1"
          rows={3}
        />
      </div>
    </div>
  );
};

// ===== COURIER/DELIVERY SERVICE FORM =====
export const CourierServiceForm = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
        <Truck className="w-5 h-5" />
        Drop-off Details
      </div>

      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-800">
          <strong>Note:</strong> Pickup details were entered in the previous step. 
          Please confirm the <strong>delivery destination</strong> details below.
        </p>
      </div>

      <div>
        <Label>Recipient Name at Drop-off</Label>
        <Input
          value={data.dropoff_recipient_name || ''}
          onChange={(e) => handleChange('dropoff_recipient_name', e.target.value)}
          placeholder="Name of person receiving the package"
          className="input-styled mt-1"
        />
      </div>

      <div>
        <Label>Recipient Phone</Label>
        <Input
          value={data.dropoff_recipient_phone || ''}
          onChange={(e) => handleChange('dropoff_recipient_phone', e.target.value)}
          placeholder="(555) 123-4567"
          className="input-styled mt-1"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Delivery Urgency</Label>
          <Select value={data.delivery_urgency || ''} onValueChange={(v) => handleChange('delivery_urgency', v)}>
            <SelectTrigger className="input-styled mt-1">
              <SelectValue placeholder="Select urgency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard (same day)</SelectItem>
              <SelectItem value="priority">Priority (within 4 hours)</SelectItem>
              <SelectItem value="express">Express (within 2 hours)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Signature Required?</Label>
          <Select value={data.signature_required?.toString() || 'false'} onValueChange={(v) => handleChange('signature_required', v === 'true')}>
            <SelectTrigger className="input-styled mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false">No - Leave at door</SelectItem>
              <SelectItem value="true">Yes - Must sign</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Drop-off Instructions</Label>
        <Textarea
          value={data.dropoff_instructions || ''}
          onChange={(e) => handleChange('dropoff_instructions', e.target.value)}
          placeholder="Gate codes, where to leave package if no one is home, special access instructions..."
          className="input-styled mt-1"
          rows={3}
        />
      </div>
    </div>
  );
};

// ===== ERRANDS SERVICE FORM =====
export const ErrandsServiceForm = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const errandTypes = ['Grocery Shopping', 'Pharmacy Pickup', 'Post Office', 'Bank', 'DMV', 'Pet Store', 'Hardware Store', 'Other Shopping'];

  const toggleErrandType = (type) => {
    const current = data.errand_types || [];
    const updated = current.includes(type) 
      ? current.filter(t => t !== type)
      : [...current, type];
    handleChange('errand_types', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
        <ShoppingBag className="w-5 h-5" />
        Errand Details
      </div>

      <div>
        <Label className="mb-3 block">What type of errands? *</Label>
        <div className="flex flex-wrap gap-2">
          {errandTypes.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => toggleErrandType(type.toLowerCase().replace(/ /g, '_'))}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                (data.errand_types || []).includes(type.toLowerCase().replace(/ /g, '_'))
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label>Detailed Task List *</Label>
        <Textarea
          value={data.task_list || ''}
          onChange={(e) => handleChange('task_list', e.target.value)}
          placeholder="List each errand/task in detail:&#10;1. Go to Walmart on Main St - buy milk, bread, eggs&#10;2. Pick up prescription at CVS - name under John Smith&#10;3. Drop off package at UPS store"
          className="input-styled mt-1"
          rows={5}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Number of Stops</Label>
          <Select value={data.num_stops?.toString() || ''} onValueChange={(v) => handleChange('num_stops', parseInt(v))}>
            <SelectTrigger className="input-styled mt-1">
              <SelectValue placeholder="Select stops" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                <SelectItem key={num} value={num.toString()}>{num} stop{num > 1 ? 's' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Preferred Store(s)</Label>
          <Input
            value={data.preferred_stores || ''}
            onChange={(e) => handleChange('preferred_stores', e.target.value)}
            placeholder="e.g., Walmart, Target, CVS"
            className="input-styled mt-1"
          />
        </div>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> If you selected "Pay Upfront" in the previous step, the budget you entered will be used for purchases.
        </p>
      </div>

      <label className="flex items-center gap-3 p-3 bg-[var(--accent)] rounded-lg cursor-pointer">
        <Checkbox
          checked={data.receipts_required || false}
          onCheckedChange={(checked) => handleChange('receipts_required', checked)}
        />
        <span>I need all receipts returned to me</span>
      </label>

      <div>
        <Label>Special Instructions</Label>
        <Textarea
          value={data.special_instructions || ''}
          onChange={(e) => handleChange('special_instructions', e.target.value)}
          placeholder="Brand preferences, size specifications, what to do if item is unavailable..."
          className="input-styled mt-1"
          rows={3}
        />
      </div>
    </div>
  );
};

// ===== TASKS SERVICE FORM =====
export const TasksServiceForm = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const taskCategories = ['Furniture Assembly', 'Mounting/Hanging', 'Minor Repairs', 'Moving Help', 'Organization', 'Yard Work', 'Tech Help', 'Other'];

  const toggleTaskCategory = (type) => {
    const current = data.task_categories || [];
    const updated = current.includes(type) 
      ? current.filter(t => t !== type)
      : [...current, type];
    handleChange('task_categories', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
        <Wrench className="w-5 h-5" />
        Task Details
      </div>

      <div>
        <Label className="mb-3 block">What type of tasks? *</Label>
        <div className="flex flex-wrap gap-2">
          {taskCategories.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => toggleTaskCategory(type.toLowerCase().replace(/ /g, '_'))}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                (data.task_categories || []).includes(type.toLowerCase().replace(/ /g, '_'))
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label>Task Description *</Label>
        <Textarea
          value={data.task_description || ''}
          onChange={(e) => handleChange('task_description', e.target.value)}
          placeholder="Describe the task(s) in detail:&#10;- What needs to be done?&#10;- Any specific requirements?&#10;- What is the expected outcome?"
          className="input-styled mt-1"
          rows={4}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Tools/Supplies Needed</Label>
          <Select value={data.tools_provided || ''} onValueChange={(v) => handleChange('tools_provided', v)}>
            <SelectTrigger className="input-styled mt-1">
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="worker_provides">Worker brings tools</SelectItem>
              <SelectItem value="customer_provides">I have all tools needed</SelectItem>
              <SelectItem value="purchase_needed">Tools need to be purchased</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Physical Difficulty</Label>
          <Select value={data.difficulty_level || ''} onValueChange={(v) => handleChange('difficulty_level', v)}>
            <SelectTrigger className="input-styled mt-1">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Light (no heavy lifting)</SelectItem>
              <SelectItem value="moderate">Moderate (some lifting)</SelectItem>
              <SelectItem value="heavy">Heavy (significant labor)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {data.tools_provided === 'purchase_needed' && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> If you selected "Pay Upfront" in the previous step, the budget you entered will be used for supplies/tools.
          </p>
        </div>
      )}

      <div>
        <Label>Special Instructions</Label>
        <Textarea
          value={data.special_instructions || ''}
          onChange={(e) => handleChange('special_instructions', e.target.value)}
          placeholder="Access information, safety concerns, or other important details..."
          className="input-styled mt-1"
          rows={3}
        />
      </div>
    </div>
  );
};

// ===== MAIN EXPORT - Service Detail Form Selector =====
const ServiceDetailForms = ({ serviceType, data, onChange }) => {
  switch (serviceType) {
    case 'cleaning':
      return <CleaningServiceForm data={data.cleaning_info || {}} onChange={(info) => onChange({ ...data, cleaning_info: info })} />;
    case 'laundry':
      return <LaundryServiceForm data={data.laundry_info || {}} onChange={(info) => onChange({ ...data, laundry_info: info })} />;
    case 'courier':
      return <CourierServiceForm data={data.courier_info || {}} onChange={(info) => onChange({ ...data, courier_info: info })} />;
    case 'errands':
      return <ErrandsServiceForm data={data.errands_info || {}} onChange={(info) => onChange({ ...data, errands_info: info })} />;
    case 'tasks':
      return <TasksServiceForm data={data.tasks_info || {}} onChange={(info) => onChange({ ...data, tasks_info: info })} />;
    default:
      return <CleaningServiceForm data={data.cleaning_info || {}} onChange={(info) => onChange({ ...data, cleaning_info: info })} />;
  }
};

export default ServiceDetailForms;
